import React, { useEffect, useMemo, useState } from 'react';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Grid from '@material-ui/core/Grid';
import { FormLabel } from '@material-ui/core';
import OptionBtoStoDropDown from './OptionBtoStoDropDown';
import QtyBasisDropDown from './QtyBasisDropDown';
import QtyValueTextField from './QtyValueTextField';
import BrokerageDropDown from './BrokerageDropDown';
import AccountNumberDropDown from './AccountNumberDropDown';
import EntryTypeDropDown from './EntryTypeDropDown';
import InstrumentDropDown from './InstrumentDropDown';
import SymbolTextField from './SymbolTextField';
import useTopReducer from '../tradingPlanFormComponents/hooks/topReducer';
import {
  useCandleBreak,
  useCandleBreakEnabled,
  useEntryTactics,
  useLimitOrder,
  useLimitOrderEnabled,
  useMarketOrder,
  useMarketOrderEnabled,
} from '../tradingPlanFormComponents/hooks/tactics/EntryTacticHooks';
import LimitOrderTextFields from './LimitOrderTextFields';
import CandleBreakTextFields from './CandleBreakTextFields';
import SelectExpirationDates from '../tradingPlanFormComponents/FieldComponents/ExpirationDates';
import {
  useExpirationDates,
  useOptionChoices,
} from '../tradingPlanFormComponents/hooks/optionmanagement/OptionManagmentHooks';
import RenderOptionChains, {
  CreateSubscriptionForOptionChains,
} from '../tradingPlanFormComponents/FieldComponents/OptionChains';
import DisplayOptionChoices from '../tradingPlanFormComponents/FieldComponents/DisplayOptionChoices';
import DisplayOptionChoice from './DisplayOptionChoice';
import isEmpty from '../../../../utils/objectchecking/IsEmpty';
import formatStringToNumDate from '../../../../utils/stringManipulation/FormatStringToNumDate';
import PositionDirectionDropDown from './PositionDirectionDropDown';
import ReactHotkeys from 'react-hot-keys';
import PushMessage from '../../../displays/pushmessages/PushMessage';
import DataSourceSelect from '../../../displays/settings/userprefs/quicktradesuserprefs/DataSourceSelect';
import DataSourceDropDown from './DataSourceDropDown';
import {
  useBreak,
  useBreakEnabled,
} from '../tradingPlanFormComponents/hooks/setup/SetupHooks';
import BreakTextFields from './BreakTextFields';
import WorkOrders from '../tradingPlanFormComponents/FieldComponents/workOrders/WorkOrders';
import Symbol from '../tradingPlanFormComponents/FieldComponents/Symbol';
import { dataValidation } from '../AddTradePlan/DataValidation';
import { addTradePlan } from '../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/addtradeplanmessage/addTradePlan';

export default function QuickTradeForm({ userPreferences, focusRef }) {
  //console.log(TradePlans);
  const [clickedSubmit, setClickedSubmit] = useState(false);
  const [submitMessageDetails, setSubmitMessageDetails] = useState({});

  const [qtyBasisValue, setQtyBasisValue] = useState(0);
  const [optionDirection, setOptionDirection] = useState('');
  const [top, dispatchTop] = useTopReducer();
  const [expirationDates, setExpirationDates] = useExpirationDates();
  const [optionChoices, setOptionChoices] = useOptionChoices();
  const [optionChains, setOptionChains] = useState({});
  // Entry Tactics
  const [marketOrderEnabled, setMarketOrderEnabled] = useMarketOrderEnabled();
  const [limitOrderEnabled, setLimitOrderEnabled] = useLimitOrderEnabled();
  const [candleBreakEnabled, setCandleBreakEnabled] = useCandleBreakEnabled();
  const [breakEnabled, setBreakEnabled] = useBreakEnabled();

  const [marketOrder, dispatchMarketOrder] = useMarketOrder();
  const [limitOrder, dispatchLimitOrder] = useLimitOrder();
  const [candleBreak, dispatchCandleBreak] = useCandleBreak();
  const [Break, dispatchBreak] = useBreak();

  const [positionDirection, setPositionDirection] = useState('');

  const setTacticQtyBasisValue = (tactic, tacticEnabled, dispatchTactic) => {
    if (tacticEnabled) {
      if (tactic.quantityBasisValue !== qtyBasisValue) {
        dispatchTactic({ quantityBasisValue: qtyBasisValue });
      }
    }
  };
  //so we can have a market order tactic, with a break setup
  if (breakEnabled) {
    if (!marketOrderEnabled) {
      setMarketOrderEnabled(true);
    }
  }

  setTacticQtyBasisValue(
    marketOrder,
    setMarketOrderEnabled,
    dispatchMarketOrder
  );
  setTacticQtyBasisValue(limitOrder, setLimitOrderEnabled, dispatchLimitOrder);
  setTacticQtyBasisValue(candleBreak, candleBreakEnabled, dispatchCandleBreak);

  if (positionDirection === 'LONG') {
    if (!top.goLong || top.goShort) {
      dispatchTop({ goLong: true, goShort: false });
    }
  } else if (positionDirection === 'SHORT') {
    if (top.goLong || !top.goShort) {
      dispatchTop({ goLong: false, goShort: true });
    }
  }

  const entryTactics = useEntryTactics(
    [marketOrderEnabled, setMarketOrderEnabled],
    [marketOrder, dispatchMarketOrder],
    [limitOrderEnabled, setLimitOrderEnabled],
    [limitOrder, dispatchLimitOrder],
    [candleBreakEnabled, setCandleBreakEnabled],
    [candleBreak, dispatchCandleBreak]
  );

  const data = useMemo(() => {
    const { ...remaining } = top;
    return {
      ...remaining,
      ...{ entryTactics: entryTactics },
      ...{ setupDetails: breakEnabled ? { BREAK: Break } : {} },
      ...{ exitTactics: userPreferences['userPreferences'].exitTactics },
      ...(top.instrument === 'option' && {
        optionManagement: {
          optionChoices: optionChoices,
          expirationDates: expirationDates,
        },
      }),
    };
  }, [top, entryTactics, breakEnabled, Break, userPreferences, optionChoices]);

  if (!breakEnabled) {
    if (top.setup !== 'AD_HOC') {
      dispatchTop({ setup: 'AD_HOC' });
    }
  } else {
    if (top.setup !== 'BREAK') {
      dispatchTop({ setup: 'BREAK' });
    }
  }

  useEffect(() => {
    let quickTradeSetup = userPreferences['userPreferences']['quickTrade'];
    //console.log(quickTradeSetup);
    const dispatchField = (field) => {
      if (
        quickTradeSetup[field] !== undefined &&
        quickTradeSetup[field] !== ''
      ) {
        dispatchTop({ [field]: quickTradeSetup[field] });
      }
    };
    dispatchField('accountNumber');
    dispatchField('brokerage');
    dispatchField('quantityBasis');
    dispatchField('instrument');
    dispatchField('dataSource');
    dispatchField('workOrders');
    dispatchField('getAwayOffsetPct');
    dispatchField('pctBetweenBidAsk');
    dispatchField('pctIncreasePerOrderAttempt');

    setQtyBasisValue(quickTradeSetup.quantityBasisValue);
    setOptionDirection(quickTradeSetup.optionDirection);
    switch (quickTradeSetup.entryType) {
      case 'MARKET_ORDER':
        setMarketOrderEnabled(true);
        setLimitOrderEnabled(false);
        setCandleBreakEnabled(false);
        setBreakEnabled(false);
        break;
      case 'LIMIT_ORDER':
        setMarketOrderEnabled(false);
        setLimitOrderEnabled(true);
        setCandleBreakEnabled(false);
        dispatchLimitOrder(quickTradeSetup.limitOrder);
        break;
      case 'CANDLE_BREAK':
        setMarketOrderEnabled(false);
        setLimitOrderEnabled(false);
        setCandleBreakEnabled(true);
        dispatchCandleBreak(quickTradeSetup.candleBreak);
        break;
      case 'BREAK':
        setBreakEnabled(true);
        setMarketOrderEnabled(true);
        setLimitOrderEnabled(false);
        setCandleBreakEnabled(false);
        dispatchBreak(quickTradeSetup['break']);
        break;
    }
  }, [userPreferences]);

  const openTrade = () => {
    console.log(data);
    let [validForm, message] = dataValidation(
      data,
      1,
      finishedRenderingOptionChain
    );
    if (!validForm) {
      setClickedSubmit(true);
      setSubmitMessageDetails({ severityScore: 'error', message: message });
      return;
    }
    //}
    if (data.optionManagement !== undefined) {
      data.optionManagement.notExpiringToday = undefined; //this is ONLY so frontend can use this. no need to pass to backend
    }
    //provide datasource so tradeplan can setup options from within json converter
    if (data.optionManagement !== undefined) {
      if (data.optionManagement.optionChoices !== undefined) {
        data.optionManagement.optionChoices.dataSource = data.dataSource;
      }
    }
    addTradePlan({
      tradePlanObject: data,
      //key: storedConfigKey,
      //configName: configTitle,
    });
    setClickedSubmit(true);
    setSubmitMessageDetails({ severityScore: 'success', message: 'submitted' });
  };

  let goLongShortCut = 'alt+l';
  let goShortShortCut = 'alt+s';
  let openTradeShortCut = 'enter';
  let symbolFocusShortCut = 'alt+n';
  let setStockShortCut = 'alt+k';
  let setOptionShortCut = 'alt+o';
  let shortcutKeyActions = {
    [goLongShortCut]: { action: () => setPositionDirection('LONG') },
    [goShortShortCut]: { action: () => setPositionDirection('SHORT') },
    [openTradeShortCut]: { action: () => openTrade() },
    [setStockShortCut]: {
      action: () => {
        dispatchTop({ instrument: 'stock' });
      },
    },
    [setOptionShortCut]: {
      action: () => {
        dispatchTop({ instrument: 'option' });
      },
    },
    [symbolFocusShortCut]: { action: () => focusRef.current.focus() },
  };

  const onKeyDown = (keyName, e, handle) => {
    shortcutKeyActions[keyName].action();
  };
  const [finishedRenderingOptionChain, setFinishedRenderingOptionChain] =
    useState(true);
  return (
    <>
      <ReactHotkeys
        keyName={
          goLongShortCut +
          ',' +
          goShortShortCut +
          ',' +
          openTradeShortCut +
          ',' +
          setStockShortCut +
          ',' +
          setOptionShortCut
        }
        onKeyDown={onKeyDown}
      >
        <Grid item>
          <br />
        </Grid>
        <Grid item container>
          <Grid item xs={2}>
            <BrokerageDropDown top={top} dispatchTop={dispatchTop} />
          </Grid>
          <Grid item xs={2}>
            <AccountNumberDropDown top={top} dispatchTop={dispatchTop} />
          </Grid>
          <Grid item xs={2}>
            <DataSourceDropDown
              dataSource={top.dataSource}
              setDataSource={(value) => {
                dispatchTop({ dataSource: value });
              }}
              setAccountNumber={(value) => {
                dispatchTop({ accountNumber: value });
              }}
              setBrokerage={(value) => {
                dispatchTop({ brokerage: value });
              }}
            />
          </Grid>
        </Grid>
        <Grid item>
          <br />
        </Grid>
        <Grid item container>
          <Grid item xs={2}>
            <QtyBasisDropDown top={top} dispatchTop={dispatchTop} />
          </Grid>
          <Grid item xs={2}>
            <QtyValueTextField
              qtyBasisValue={qtyBasisValue}
              setQtyBasisValue={setQtyBasisValue}
            />
          </Grid>
          <Grid item xs={2}>
            <EntryTypeDropDown
              marketOrderEnabled={marketOrderEnabled}
              setMarketOrderEnabled={setMarketOrderEnabled}
              limitOrderEnabled={limitOrderEnabled}
              setLimitOrderEnabled={setLimitOrderEnabled}
              candleBreakEnabled={candleBreakEnabled}
              setCandleBreakEnabled={setCandleBreakEnabled}
              breakEnabled={breakEnabled}
              setBreakEnabled={setBreakEnabled}
            />
          </Grid>
          <Grid item xs={6}>
            {marketOrderEnabled && !breakEnabled ? (
              <></>
            ) : limitOrderEnabled ? (
              <LimitOrderTextFields
                limitOrder={limitOrder}
                dispatchLimitOrder={dispatchLimitOrder}
              />
            ) : candleBreakEnabled ? (
              <CandleBreakTextFields
                candleBreak={candleBreak}
                dispatchCandleBreak={dispatchCandleBreak}
              />
            ) : breakEnabled ? (
              <BreakTextFields
                breakPrice={Break}
                dispatchBreak={dispatchBreak}
              />
            ) : (
              <></>
            )}
          </Grid>
        </Grid>
        <Grid item>
          <br />
        </Grid>
        <Grid item Container>
          <WorkOrders top={top} dispatchTop={dispatchTop} />
        </Grid>
        <Grid item container>
          <Grid item>
            <br />
          </Grid>
          <Grid item xs={2}>
            <InstrumentDropDown top={top} dispatchTop={dispatchTop} />
          </Grid>
          <Grid item xs={2}>
            <SymbolTextField
              top={top}
              dispatchTop={dispatchTop}
              focusRef={focusRef}
              setPositionDirection={setPositionDirection}
              finishedRenderingOptionChain={finishedRenderingOptionChain}
              setFinishedRenderingOptionChain={setFinishedRenderingOptionChain}
            />
          </Grid>
          <Grid item xs={2}>
            <PositionDirectionDropDown
              positionDirection={positionDirection}
              setPositionDirection={setPositionDirection}
              symbol={top.symbol}
              dataSource={top.dataSource}
            />
          </Grid>
        </Grid>
        <Grid item>
          <br />
        </Grid>
        {top.instrument === 'option' ? (
          <Grid item container>
            <RenderQuickTradeFormOptionComponents
              optionChains={optionChains}
              setOptionChains={setOptionChains}
              optionChoices={optionChoices}
              setOptionChoices={setOptionChoices}
              expirationDates={expirationDates}
              setExpirationDates={setExpirationDates}
              userPreferences={userPreferences}
              top={top}
              positionDirection={positionDirection}
              optionDirection={optionDirection}
              setOptionDirection={setOptionDirection}
              finishedRenderingOptionChain={finishedRenderingOptionChain}
              setFinishedRenderingOptionChain={setFinishedRenderingOptionChain}
            />
          </Grid>
        ) : (
          <></>
        )}
        <input
          type="submit"
          className="btn btn-primary m-2 addTradePlan"
          value={'OPEN TRADE'}
          onClick={(evt) => {
            evt.preventDefault();
            openTrade();
          }}
        />
        {
          <PushMessage
            message={submitMessageDetails['message']}
            severityScore={submitMessageDetails['severityScore']}
            open={clickedSubmit}
            setOpen={setClickedSubmit}
          />
        }
      </ReactHotkeys>
    </>
  );
}

function RenderQuickTradeFormOptionComponents({
  optionChains,
  setOptionChains,
  optionChoices,
  setOptionChoices,
  expirationDates,
  setExpirationDates,
  optionDirection,
  setOptionDirection,
  userPreferences,
  top,
  positionDirection,
  finishedRenderingOptionChain,
  setFinishedRenderingOptionChain,
}) {
  useEffect(() => {
    if (!isEmpty(optionChains)) {
      let quickTradeSetup = userPreferences['userPreferences']['quickTrade'];
      if (positionDirection !== 'LONG' && positionDirection !== 'SHORT') {
        console.log('position direction not set');
        return;
      }
      if (
        optionDirection !== 'buy to open' &&
        optionDirection !== 'sell to open'
      ) {
        console.log('option direction not set');
        return;
      }

      //            //console.log(optionChains);
      optionChoices = {};
      let lastQuote = optionChains['quote'];
      let approxCurrentPrice = (lastQuote['bid'] + lastQuote['ask']) / 2;
      //      console.log(approxCurrentPrice);
      //need to request chain that user wants at first, not automatically the first one

      let date = formatStringToNumDate(
        expirationDates[0]
      ); /*i tell it to only start paying attention at
                            a starting index (ie first chain im actually paying attention to) in the component*/
      //  console.log(date);
      let chain =
        optionChains['chains'][0][
          date
        ]; /*i tell it to only start paying attention at
                            a starting index (ie first chain im actually paying attention to) in the component*/
      // console.log(chain);
      if (chain !== undefined) {
        let type = '';
        let optionsToSelectFrom = [];
        if (positionDirection === 'LONG') {
          if (optionDirection === 'buy to open') {
            //            console.log("long buy to open calls");
            optionsToSelectFrom = chain.filter(
              (option) =>
                /* JSON.parse(option) */ option['option_type'] === 'call'
            );
            type = 'call';
          } else {
            //          console.log("long sell to open puts");
            optionsToSelectFrom = chain.filter(
              (option) =>
                /* JSON.parse(option) */ option['option_type'] === 'put'
            );
            type = 'put';
            //optionsToSelectFrom.reverse(); //for finding itm and otm, make sure you start from end
          }
        } else {
          if (optionDirection === 'buy to open') {
            //        console.log("short buy to open puts");
            optionsToSelectFrom = chain.filter(
              (option) =>
                /* JSON.parse(option) */ option['option_type'] === 'put'
            );
            type = 'put';
            //optionsToSelectFrom.reverse(); //for finding itm and otm, make sure you start from end
          } else {
            //      console.log("short sell to open calls");
            optionsToSelectFrom = chain.filter(
              (option) =>
                /* JSON.parse(option) */ option['option_type'] === 'call'
            );
            type = 'call';
          }
        }
        if (type === 'call') {
          for (let num in optionsToSelectFrom) {
            let option = /* JSON.parse( */ optionsToSelectFrom[num]; /* ) */
            if (option['strike'] > approxCurrentPrice) {
              if (quickTradeSetup['maxOptionPrice'] > 0) {
                if (
                  (option.bid + option.ask) / 2 >
                  quickTradeSetup['maxOptionPrice']
                ) {
                  continue;
                }
              }
              let optionChain = undefined;
              if (optionChoices.choices === undefined) {
                optionChoices.choices = {};
              } else {
                optionChain = optionChoices.choices[date];
              }
              if (optionChain === undefined) {
                optionChain = [];
              }
              if (quickTradeSetup.nearestOtmItm === 'nearestItm') {
                let optionToPush =
                  /* JSON.parse( */
                  optionsToSelectFrom[num - 1 > 0 ? num - 1 : 0];
                /* ) */ if (approxCurrentPrice > optionToPush['strike']) {
                  //this would occur if, due to the max option price range, an option itm is not possible
                  optionChain.push(optionToPush);
                  optionChoices.choices[date] = optionChain;
                  setOptionChoices(optionChoices);
                }
              } else if (quickTradeSetup.nearestOtmItm === 'nearestOtm') {
                optionChain.push(option.symbol);
                optionChoices.choices[date] = optionChain;
                setOptionChoices(optionChoices);
              }
              break;
            }
          }
        } else {
          //if put
          for (let num = optionsToSelectFrom.length - 1; num > 0; num--) {
            let option = /* JSON.parse( */ optionsToSelectFrom[num]; /* ) */
            if (option['strike'] < approxCurrentPrice) {
              if (quickTradeSetup['maxOptionPrice'] > 0) {
                if (
                  (option.bid + option.ask) / 2 >
                  quickTradeSetup['maxOptionPrice']
                ) {
                  continue;
                }
              }
              let optionChain = undefined;
              if (optionChoices.choices === undefined) {
                optionChoices.choices = {};
              } else {
                optionChain = optionChoices.choices[date];
              }
              if (optionChain === undefined) {
                optionChain = [];
              }
              if (quickTradeSetup.nearestOtmItm === 'nearestItm') {
                let optionToPush =
                  /* JSON.parse( */
                  optionsToSelectFrom[
                    num + 1 < optionsToSelectFrom.length - 1
                      ? num + 1
                      : optionsToSelectFrom.length - 1
                  ];

                if (approxCurrentPrice < optionToPush['strike']) {
                  //this would occur if, due to the max option price range, an option itm is not possible
                  optionChain.push(optionToPush);
                  optionChoices.choices[date] = optionChain;
                  setOptionChoices(optionChoices);
                }
              } else if (quickTradeSetup.nearestOtmItm === 'nearestOtm') {
                optionChain.push(option.symbol);
                optionChoices.choices[date] = optionChain;
                setOptionChoices(optionChoices);
              }
              break;
            }
          }
        }
      } else {
        console.log('chain is undefined!');
        console.log(date);
        //console.log(formatStringToNumDate(date));
        console.log(optionChains['chains']);
        console.log(optionChains['chains'][0]);
        console.log(optionChains['chains'][0][date]);
      }
      //console.log(optionChoices);
    }
    if (!finishedRenderingOptionChain) {
      console.log('acknowledge finished chain');
      setFinishedRenderingOptionChain(true);
    }
  }, [optionChains, userPreferences, positionDirection, optionDirection]);
  //console.log(optionChoices);
  return (
    <>
      <Grid item xs={2}>
        <OptionBtoStoDropDown
          optionDirection={optionDirection}
          setOptionDirection={setOptionDirection}
        />
      </Grid>
      <Grid item xs={2}>
        <SelectExpirationDates
          symbol={top.symbol}
          brokerage={top.brokerage}
          dataSource={top.dataSource}
          expirationDates={expirationDates}
          setExpirationDates={setExpirationDates}
          isMulti={false}
          numOfDefaultSelected={1}
          setOptionChains={setOptionChains}
          showStrikesMessage={false}
          notExpiringToday={
            userPreferences['userPreferences']['quickTrade']['notExpiringToday']
          }
          setNotExpiringToday={
            userPreferences['userPreferences']['quickTrade']
              .setOptionNotExpiringToday
          }
          startingIndex={
            userPreferences['userPreferences']['quickTrade']
              .expirationWeeksAway !== undefined
              ? userPreferences['userPreferences']['quickTrade']
                  .expirationWeeksAway
              : 0
          }
        />
      </Grid>

      <Grid item xs={1} />
      <Grid item xs={2}>
        <CreateSubscriptionForOptionChains
          symbol={top.symbol}
          dataSource={top.dataSource}
          brokerage={top.brokerage}
          expirationDates={expirationDates}
          optionChains={[optionChains, setOptionChains]}
          finishedRenderingOptionChain={finishedRenderingOptionChain}
          setFinishedRenderingOptionChain={setFinishedRenderingOptionChain}
        />
        <RenderOptionChains
          top={top}
          expirationDates={expirationDates}
          optionChoices={[optionChoices, setOptionChoices]}
          setSymbolsUsed={() => {}}
          optionChains={[optionChains, setOptionChains]}
          selectSingle={true}
          displayOptionComponent={DisplayOptionChoice}
          showSelectedOptions
        />
      </Grid>
    </>
  );
}
